import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { makeStyles, styled } from '@material-ui/styles'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import { useSelector } from 'react-redux'
import StoragePoolsPicklist from '../pools/StoragePoolsPicklist'
import PicklistField from 'core/components/validatedForm/DropdownField'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import Info from 'core/components/validatedForm/Info'
import Theme from 'core/themes/model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import { migrateVolume } from './actions'

const Container = styled('div')({
  gap: 16,
  display: 'grid',
})

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  warning: {
    display: 'grid',
    gap: 8,
  },
  icon: {
    marginRight: 8,
  },
  step: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    gap: 8,
  },
}))

export default function MigrateVolumeDialog({ rows: [volume], onClose }) {
  const isInUse = volume?.status === 'in-use'
  const classes = useStyles()
  const { params, getParamsUpdater } = useParams({
    storagePool: null,
    confirmBypass: false,
  })
  const { update, updating, error, reset } = useUpdateAction(migrateVolume)

  const handleSubmit = useCallback(async () => {
    const withoutPoolName = params.storagePool?.split('#')?.[0]
    const body = {
      'os-migrate_volume': {
        host: withoutPoolName,
      },
    }
    const { success } = await update({
      id: volume?.id,
      body,
    })
    if (success) handleClose()
  }, [volume, params])

  const handleClose = () => {
    reset()
    onClose(true)
  }

  const storagePoolFilterFn = useCallback(
    (options) => {
      return options.filter((option) => {
        const isSameBackend = option.value === volume['os-vol-host-attr:host']
        const matchingVolumeType =
          option.value?.split('#')?.[1] === volume?.['os-vol-host-attr:host']?.split('#')?.[1]
        return !isSameBackend && matchingVolumeType
      })
    },
    [volume['os-vol-host-attr:host']],
  )

  return (
    <ModalForm
      title="Migrate Volume"
      onSubmit={handleSubmit}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Migrate Volume`}
      panel="dialog"
      maxWidth={800}
      disableSubmit={isInUse ? !params.confirmBypass : false}
      loading={false}
      open
    >
      <ServiceUnhealthyInfo action="Volume Migrate" blockStorage />
      <Container>
        <Text variant="body2">
          Migrate this volume from one storage backend to another within the same volume type.
        </Text>
        {isInUse && (
          <Info error>
            <div className={classes.warning}>
              <Text variant="body2">
                <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
                <b>WARNING</b>
              </Text>
              <Text variant="body2">
                The migration of an <b>in-use</b> volume is generally not recommended if the volume
                has more than one active read/write attachment.
              </Text>
            </div>
          </Info>
        )}
        <Text variant="body2">
          Select a new host/backend combination to migrate this volume to. Only running backends
          that belong to the same volume type as the selected volume will be selectable.
        </Text>
        <PicklistField
          DropdownComponent={StoragePoolsPicklist}
          id="storagePool"
          onChange={getParamsUpdater('storagePool')}
          value={params.storagePool}
          label="Storage Backend"
          filterFn={storagePoolFilterFn}
          required
        />
        {isInUse ? (
          <CheckboxField
            id="confirmBypass"
            label="I understand the risks of migrating an in-use volume and would like to proceed forward"
            value={params.confirmBypass}
            onChange={getParamsUpdater('confirmBypass')}
            required
          />
        ) : (
          // Some spacing under the picklist
          <br />
        )}
      </Container>
    </ModalForm>
  )
}
